import { belgeUyariKarti } from "../components/documents.js";
import { hayatiRisk, saglikTakipli } from "../domain/students.js";
import { DONEM } from "../config.js";
import { B, stu } from "../domain/context.js";
import { acikFaturalar, faturaDurumu, totalDue, totalPaid } from "../domain/invoices.js";
import { age, donemAdi, esc, fmtDate, money } from "../lib/fmt.js";
import { avatar } from "../lib/avatar.js";
import { card, classChip, emptyNote, sectionTitle } from "../components/ui.js";

// ---------- PANEL ----------
export function viewPanel() {
  const t = B();
  const att = Object.values(t.attendance);
  const here = att.filter(a => a.status === "geldi").length;
  const izinli = att.filter(a => a.status === "izinli").length;
  const yok = att.filter(a => a.status === "gelmedi").length;
  const staffActive = t.staff.filter(p => p.status === "aktif").length;
  const acik = acikFaturalar(t);
  const overdue = acik.filter(p => faturaDurumu(stu(p.studentId), p) !== "odendi");
  const unread = t.messages.filter(m => m.unread);

  const month = new Date().getMonth() + 1;
  const bdays = t.students.filter(s => Number(s.birthDate.split("-")[1]) === month);
  // Sağlık uyarıları artık nottaki kelimeden değil yapısal veriden geliyor;
  // hayati riskli çocuklar listenin başında.
  const alerts = saglikTakipli(t.students)
    .sort((a, b) => Number(hayatiRisk(b)) - Number(hayatiRisk(a)));

  const stats = [
    { label: "Bugün Gelen", value: here, sub: `${t.students.length} kayıtlı öğrenci`, color: "teal" },
    { label: "İzinli", value: izinli, sub: "veli bildirimi ile", color: "sun" },
    { label: "Gelmeyen", value: yok, sub: "veli aranacak", color: "coral" },
    { label: "Görevli Personel", value: staffActive, sub: `${t.staff.length} toplam personel`, color: "grape" },
  ];

  const rhythm = [
    { time: "07:30", label: "Karşılama & Serbest Oyun", icon: "🚌" },
    { time: "09:00", label: "Kahvaltı", icon: "🥐" },
    { time: "10:00", label: "Etkinlik Saati", icon: "🎨" },
    { time: "11:45", label: "Öğle Yemeği", icon: "🍲" },
    { time: "12:30", label: "Uyku / Dinlenme", icon: "😴" },
    { time: "14:30", label: "İkindi & Bahçe", icon: "☀️" },
    { time: "16:30", label: "Veliye Teslim", icon: "👋" },
  ];
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const toMin = t => Number(t.split(":")[0]) * 60 + Number(t.split(":")[1]);
  const currentIdx = rhythm.findIndex((r, i) => {
    const next = rhythm[i + 1];
    return nowMin >= toMin(r.time) && (!next || nowMin < toMin(next.time));
  });

  return `
  <div class="grid gap-3 sm:gap-4 grid-cols-2 xl:grid-cols-4">
    ${stats.map(s => card(`
      <div class="p-4">
        <div class="text-xs font-semibold text-inksoft">${s.label}</div>
        <div class="font-display font-extrabold text-3xl mt-1 text-${s.color}">${s.value}</div>
        <div class="text-[11px] text-inksoft/80 mt-0.5">${s.sub}</div>
      </div>`, "min-w-0")).join("")}
  </div>

  ${card(`
    <div class="p-5">
      ${sectionTitle("Günün Ritmi", `<span class="text-[11px] text-inksoft">${now.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })} itibarıyla</span>`)}
      <div class="flex gap-2 overflow-x-auto pb-1">
        ${rhythm.map((r, i) => `
          <div class="min-w-[108px] flex-1 rounded-xl p-3 text-center border
            ${i === currentIdx ? "bg-teal text-white border-teal shadow-card" : i < currentIdx ? "bg-paper border-line text-inksoft" : "bg-card border-line"}">
            <div class="text-xl">${r.icon}</div>
            <div class="text-[11px] font-bold mt-1 ${i === currentIdx ? "text-white/80" : "text-inksoft"}">${r.time}</div>
            <div class="text-xs font-semibold leading-tight mt-0.5">${r.label}</div>
            ${i === currentIdx ? `<div class="text-[10px] mt-1 font-bold uppercase tracking-wide text-white/85">Şu an</div>` : ""}
          </div>`).join("")}
      </div>
    </div>`, "mt-4")}

  <div class="grid gap-4 lg:grid-cols-3 mt-4">
    <div class="min-w-0 lg:col-span-2 space-y-4">
      ${card(`
        <div class="p-5">
          ${sectionTitle("Sınıf Doluluğu", `<button data-goto="siniflar" class="text-xs font-semibold text-teal hover:underline">Tümü →</button>`)}
          <div class="space-y-3">
            ${t.classes.map(c => {
              const n = t.students.filter(s => s.classId === c.id).length;
              const pct = Math.round(n / c.capacity * 100);
              return `<div>
                <div class="flex items-center justify-between text-sm mb-1">
                  <span class="font-semibold flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full" style="background:${c.color}"></span>${esc(c.name)}
                    <span class="text-[11px] text-inksoft font-normal">${c.age}</span></span>
                  <span class="text-xs text-inksoft">${n}/${c.capacity}</span>
                </div>
                <div class="h-2 rounded-full bg-paper overflow-hidden">
                  <div class="h-full rounded-full" style="width:${pct}%;background:${c.color}"></div>
                </div>
              </div>`;
            }).join("")}
          </div>
        </div>`)}

      ${card(`
        <div class="p-5">
          ${sectionTitle("Sağlık & Alerji Uyarıları", `<span class="text-[11px] rounded-full bg-coral-soft text-coral font-bold px-2 py-0.5">${alerts.length} kayıt</span>`)}
          <div class="divide-y divide-line/70">
            ${alerts.slice(0, 6).map(s => `
              <button data-student="${s.id}" class="w-full flex items-center gap-3 py-2.5 text-left hover:bg-paper/70 rounded-lg px-2 -mx-2">
                ${avatar(s)}
                <span class="min-w-0 flex-1">
                  <span class="block text-sm font-semibold truncate">${esc(s.name)}</span>
                  <span class="block text-[11px] text-inksoft truncate">${s.allergies.length ? "Alerji: " + s.allergies.join(", ") : ""}${s.allergies.length && s.note ? " · " : ""}${esc(s.note)}</span>
                </span>
                ${classChip(s.classId)}
              </button>`).join("") || emptyNote("Kayıtlı uyarı yok 🎉")}
          </div>
          ${alerts.length > 6 ? `<button data-goto="ogrenciler" class="w-full text-xs font-semibold text-teal hover:underline pt-3">${alerts.length - 6} kayıt daha →</button>` : ""}
        </div>`)}
    </div>

    <div class="min-w-0 space-y-4">
      ${card(`
        <div class="p-5">
          ${sectionTitle("Okunmamış Mesajlar", `<button data-goto="mesajlar" class="text-xs font-semibold text-teal hover:underline">Tümü →</button>`)}
          ${unread.map(m => `
            <button data-goto="mesajlar" class="w-full text-left py-2 border-b border-line/60 last:border-0">
              <div class="flex items-center justify-between">
                <span class="text-sm font-semibold">${esc(m.parent)}</span>
                <span class="text-[10px] text-inksoft">${m.time}</span>
              </div>
              <p class="text-xs text-inksoft line-clamp-2 mt-0.5">${esc(m.preview)}</p>
            </button>`).join("") || emptyNote("Tüm mesajlar yanıtlandı ✅")}
        </div>`)}

      ${belgeUyariKarti(t.id, { limit: 5 })}

      ${card(`
        <div class="p-5">
          ${sectionTitle("Bu Ayın Doğum Günleri 🎂", bdays.length ? `<span class="text-[11px] text-inksoft">${bdays.length} çocuk</span>` : "")}
          ${bdays.map(s => `
            <div class="flex items-center gap-3 py-2">
              ${avatar(s)}
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-semibold truncate">${esc(s.name)}</span>
                <span class="block text-[11px] text-inksoft">${fmtDate(new Date().getFullYear() + s.birthDate.slice(4))} · ${age(s.birthDate)} olacak</span>
              </span>
            </div>`).join("") || emptyNote("Bu ay doğum günü yok")}
        </div>`)}

      ${card(`
        <div class="p-5">
          ${sectionTitle("Tahsilat Durumu", `<button data-goto="odemeler" class="text-xs font-semibold text-teal hover:underline">Detay →</button>`)}
          <div class="text-sm text-inksoft">Bekleyen + geciken</div>
          <div class="font-display font-extrabold text-2xl text-coral">${money(totalDue(overdue) - totalPaid(overdue))}</div>
          <div class="text-[11px] text-inksoft mt-1">${overdue.length} fatura için takip gerekiyor</div>
          <div class="text-[11px] text-inksoft mt-3 pt-3 border-t border-line/70">
            ${donemAdi(DONEM)} tahakkuku <span class="font-semibold text-ink">${money(totalDue(t.payments.filter(p => p.period === DONEM)))}</span>
          </div>
        </div>`)}
    </div>
  </div>`;
}

